/**
 * PrecisionAdverseMediaPage
 * =========================
 * Route   : /adverse-media/precision
 * Roles   : super_admin · compliance_officer  (gated in-page; ProtectedRoute
 *           handles login)
 * Backend : POST /api/adverse-media/precision-analyze
 *
 * Add to App.js:
 *   <Route path="/adverse-media/precision" element={
 *     <ProtectedRoute>
 *       <DashboardLayout>
 *         <PrecisionAdverseMediaPage />
 *       </DashboardLayout>
 *     </ProtectedRoute>
 *   } />
 *
 * Rudrik uses cookie-based JWT auth (axios `withCredentials: true`) — there is
 * no bearer-token getter in this codebase, so this page follows the same
 * convention as every other Rudrik page.
 */
import React, { useState } from "react";
import axios from "axios";
import {
  AlertTriangle, Check, X, Search, Shield, FileJson, Printer,
  Sparkles, ExternalLink, Loader2,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;
const ENDPOINT = `${API}/adverse-media/precision-analyze`;

const ALLOWED_ROLES = ["super_admin", "compliance_officer"];

const INDIA_FLAG_LABELS = {
  ed_pmla:         "ED PMLA",
  cbi_fir:         "CBI FIR",
  sfio:            "SFIO",
  sebi_ban:        "SEBI Ban",
  rbi_defaulter:   "RBI Defaulter",
  mca21_strikeoff: "MCA21 Strikeoff",
  feo_declared:    "Fugitive Offender",
  interpol_red:    "Interpol Red Notice",
};
const INDIA_FLAG_KEYS = Object.keys(INDIA_FLAG_LABELS);

const CATEGORY_STYLES = {
  FRAUD:                "bg-red-900/40 text-red-200",
  AML_MONEY_LAUNDERING: "bg-orange-900/40 text-orange-200",
  SANCTIONS:            "bg-purple-900/40 text-purple-200",
  COURT_CRIMINAL:       "bg-indigo-900/40 text-indigo-200",
  REGULATORY_ACTION:    "bg-yellow-900/40 text-yellow-200",
  TAX_EVASION:          "bg-pink-900/40 text-pink-200",
  CORRUPTION:           "bg-rose-900/40 text-rose-200",
  REPUTATIONAL_ONLY:    "bg-gray-700 text-gray-300",
  FALSE_POSITIVE:       "bg-gray-800 text-gray-400",
};

const TIER_STYLES = {
  1: "bg-green-900/40 text-green-200",
  2: "bg-blue-900/40 text-blue-200",
  3: "bg-yellow-900/40 text-yellow-200",
  4: "bg-red-900/40 text-red-200",
};

const CONFIDENCE_STYLES = {
  HIGH:   "bg-green-900/40 text-green-200",
  MEDIUM: "bg-amber-900/40 text-amber-200",
  LOW:    "bg-red-900/40 text-red-200",
};

const DEMO_DATA = {
  subject_name: "Nirav Modi",
  subject_type: "PERSON",
  subject_context: "Diamantaire, Mumbai, Firestar Diamond",
  articles: [
    "The Enforcement Directorate filed a fresh chargesheet against Nirav Modi, the Mumbai-based diamantaire and founder of Firestar Diamond, alleging money laundering of Rs 6,498 crore through Punjab National Bank (PNB) under PMLA. (Economic Times, 2025-11-20)",
    "CBI registered an FIR against Nirav Modi for criminal conspiracy and cheating in connection with the PNB fraud case. The agency has also issued a Letter Rogatory to the UK. (The Hindu, 2025-09-12)",
    "Interpol issued a Red Notice for Nirav Modi at India's request. The diamantaire is currently in HMP Wandsworth in the UK fighting extradition. (NDTV, 2025-08-04)",
  ].join("\n\n---\n\n"),
};

const INITIAL = { subject_name: "", subject_type: "PERSON",
                   subject_context: "", articles: "" };


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function riskBarColor(score) {
  if (score >= 70) return "bg-red-500";
  if (score >= 40) return "bg-amber-500";
  return "bg-green-500";
}
function riskTextColor(score) {
  if (score >= 70) return "text-red-400";
  if (score >= 40) return "text-amber-300";
  return "text-green-400";
}

function deriveVerdict(composite, anyFlag, anyCritical) {
  if (anyCritical || composite >= 80) return "CRITICAL";
  if (composite >= 60 || anyFlag)     return "HIGH_RISK";
  if (composite >= 30)                return "REVIEW_REQUIRED";
  return "CLEAR";
}

const VERDICT_STYLES = {
  CLEAR:            "bg-green-900/40 text-green-200",
  REVIEW_REQUIRED:  "bg-amber-900/40 text-amber-200",
  HIGH_RISK:        "bg-red-900/40 text-red-200",
  CRITICAL:         "bg-red-900/40 text-red-200 animate-pulse",
};


// ---------------------------------------------------------------------------
// API caller — POSTs each article block separately, aggregates into a single
// shape the UI can render. The precision-analyze endpoint takes ONE article at
// a time per the backend contract.
// ---------------------------------------------------------------------------

function splitArticles(raw) {
  // Honour an explicit "---" delimiter; otherwise treat the whole textarea as
  // a single article.
  const parts = String(raw || "")
    .split(/\n\s*-{3,}\s*\n/)
    .map(s => s.trim())
    .filter(Boolean);
  return parts.length ? parts : [String(raw || "").trim()].filter(Boolean);
}

async function runPrecisionScan(form) {
  const blobs = splitArticles(form.articles);
  if (!blobs.length) {
    throw new Error("Paste at least one article to screen.");
  }
  // Run sequentially — the precision route is LLM-backed and 3-5s per call.
  // Parallel calls would burn the Universal Key budget faster without UX gain.
  const articles = [];
  for (let i = 0; i < blobs.length; i++) {
    const { data } = await axios.post(
      ENDPOINT,
      {
        subject_name:    form.subject_name.trim(),
        subject_type:    form.subject_type,
        subject_context: form.subject_context.trim() || null,
        article_text:    blobs[i],
        article_title:   `Article ${i + 1}`,
        corroborating_source_count: Math.min(blobs.length, 3),
      },
      { withCredentials: true },
    );
    articles.push(data);
  }
  return articles;
}


// ---------------------------------------------------------------------------
// Skeleton card during loading
// ---------------------------------------------------------------------------

function SkeletonCard() {
  return (
    <div className="rounded-xl border border-gray-700 bg-[#0d1117] p-5 space-y-3 animate-pulse">
      <div className="h-4 w-3/4 bg-gray-700 rounded" />
      <div className="h-3 w-1/2 bg-gray-700 rounded" />
      <div className="flex gap-2">
        <div className="h-5 w-16 bg-gray-700 rounded-full" />
        <div className="h-5 w-20 bg-gray-700 rounded-full" />
      </div>
      <div className="h-2 w-full bg-gray-700 rounded" />
    </div>
  );
}


// ---------------------------------------------------------------------------
// India flag chip
// ---------------------------------------------------------------------------

function FlagChip({ flagKey, isLit, size = "md", testIdPrefix = "india-flag" }) {
  const cls = isLit
    ? "bg-red-900/40 text-red-200 border border-red-700"
    : "bg-gray-800 text-gray-500 border border-gray-700";
  const pad = size === "sm" ? "px-2 py-0.5 text-xs" : "px-3 py-1 text-xs";
  return (
    <span data-testid={`${testIdPrefix}-${flagKey}`}
          data-lit={isLit ? "1" : "0"}
          className={`rounded-full font-semibold ${pad} ${cls}`}>
      {INDIA_FLAG_LABELS[flagKey]}
    </span>
  );
}


// ---------------------------------------------------------------------------
// Article card
// ---------------------------------------------------------------------------

function ArticleCard({ idx, item }) {
  const a = item.article_analysis || {};
  const subj = item.subject || {};
  const bd = a.risk_score_breakdown || {};
  const score = bd.final_risk_score ?? 0;
  const fp = !!a.false_positive;
  const flags = a.india_flags || {};
  const litFlags = INDIA_FLAG_KEYS.filter(k => flags[k]);

  return (
    <div
      data-testid={`article-card-${idx}`}
      data-false-positive={fp ? "1" : "0"}
      className={`rounded-xl border bg-[#0d1117] border-gray-700
                  ${fp ? "opacity-40" : ""}
                  p-5 space-y-4`}
    >
      {fp && (
        <div className="-mx-5 -mt-5 mb-2 px-5 py-2 bg-gray-800
                        border-b border-gray-700
                        text-xs font-semibold text-gray-300
                        flex items-center gap-2">
          <AlertTriangle size={14} />
          False Positive — Excluded from Score
          {a.false_positive_reason && (
            <span className="font-normal text-gray-400">
              · {a.false_positive_reason}
            </span>
          )}
        </div>
      )}

      <div>
        <div className="text-sm font-semibold text-gray-100 line-clamp-2">
          {a.headline || `Article ${idx + 1}`}
        </div>
        <div className="text-xs text-gray-400 mt-1
                        flex items-center gap-2 flex-wrap">
          <span>{a.source || "Unknown source"}</span>
          {a.published_date && <span>· {a.published_date}</span>}
          {a.url && (
            <a href={a.url} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1 text-blue-400 hover:underline">
              <ExternalLink size={12} /> view
            </a>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${TIER_STYLES[a.source_tier] || TIER_STYLES[4]}`}>
          TIER {a.source_tier ?? "?"}
        </span>
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${CATEGORY_STYLES[a.category] || CATEGORY_STYLES.REPUTATIONAL_ONLY}`}>
          {a.category || "—"}
        </span>
        {subj.is_same_entity ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                           text-xs font-semibold bg-green-900/40 text-green-200">
            <Check size={12} /> Entity confirmed
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                           text-xs font-semibold bg-red-900/40 text-red-200">
            <X size={12} /> Entity mismatch — excluded
          </span>
        )}
      </div>

      {!fp && (
        <div>
          <div className="flex items-center justify-between text-xs text-gray-300 mb-1">
            <span>Risk Score</span>
            <span className={`font-bold ${riskTextColor(score)}`}>{score}</span>
          </div>
          <div className="h-2 w-full rounded-full bg-gray-700 overflow-hidden">
            <div className={`h-full transition-all ${riskBarColor(score)}`}
                 style={{ width: `${Math.min(100, Math.max(0, score))}%` }}
                 data-testid={`risk-bar-${idx}`} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 text-xs
                          text-gray-400">
            <span>Source: <b className="text-gray-200">{bd.source_credibility ?? 0}</b></span>
            <span>Recency: <b className="text-gray-200">{bd.recency ?? 0}</b></span>
            <span>Severity: <b className="text-gray-200">{bd.severity ?? 0}</b></span>
            <span>Corroboration: <b className="text-gray-200">{bd.corroboration ?? 0}</b></span>
          </div>
        </div>
      )}

      {fp && (
        <div className="text-xs text-gray-400">Risk Score: —</div>
      )}

      {litFlags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {litFlags.map(k => (
            <FlagChip key={k} flagKey={k} isLit size="sm"
                       testIdPrefix={`article-flag-${idx}`} />
          ))}
        </div>
      )}

      {(a.key_allegations || []).length > 0 && (
        <ul className="list-disc pl-5 text-sm text-gray-300 space-y-1">
          {(a.key_allegations || []).slice(0, 3).map((al, i) => <li key={i}>{al}</li>)}
        </ul>
      )}

      <div className="flex items-center justify-between pt-2 border-t border-gray-800">
        <span className="text-[11px] text-gray-400">{item.screening_id}</span>
        <span data-testid={`confidence-badge-${idx}`}
              className={`px-2 py-0.5 rounded-full text-xs font-semibold ${CONFIDENCE_STYLES[a.overall_confidence] || CONFIDENCE_STYLES.LOW}`}>
          {a.overall_confidence || "LOW"}
        </span>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Summary panel
// ---------------------------------------------------------------------------

function SummaryPanel({ articles }) {
  const total = articles.length;
  const fps = articles.filter(a => a.article_analysis?.false_positive).length;
  const relevant = total - fps;

  // Composite = max final_risk_score across non-FP articles.
  const scores = articles
    .filter(a => !a.article_analysis?.false_positive)
    .map(a => a.article_analysis?.risk_score_breakdown?.final_risk_score || 0);
  const composite = scores.length ? Math.max(...scores) : 0;

  // Aggregate India flags — any flag true on any article lights it up.
  const aggFlags = {};
  for (const k of INDIA_FLAG_KEYS) {
    aggFlags[k] = articles.some(a => a.article_analysis?.india_flags?.[k]);
  }
  const anyFlag = Object.values(aggFlags).some(Boolean);
  const anyCritical = articles.some(a => a.article_analysis?.risk_score_breakdown?.final_risk_score >= 90);
  const verdict = deriveVerdict(composite, anyFlag, anyCritical);

  // Highest-confidence statement wins for the summary line.
  const confidences = articles.map(a => a.article_analysis?.overall_confidence).filter(Boolean);
  const overallConfidence =
    confidences.includes("HIGH") ? "HIGH"
    : confidences.includes("MEDIUM") ? "MEDIUM"
    : "LOW";
  const confidenceReason = articles.find(a => a.article_analysis?.overall_confidence === overallConfidence)
    ?.article_analysis?.confidence_reason || "";

  return (
    <div data-testid="summary-panel"
         className="rounded-2xl border border-gray-700 bg-[#0d1117] p-6 space-y-5 shadow-sm">
      <div className="flex flex-wrap items-center gap-3">
        <span data-testid="verdict-chip"
              className={`px-3 py-1 rounded-full border text-sm font-bold tracking-wide ${VERDICT_STYLES[verdict]}`}>
          {verdict.replace("_", " ")}
        </span>
        <span data-testid="composite-risk-score"
              className={`text-5xl font-extrabold leading-none ${riskTextColor(composite)}`}>
          {composite}
        </span>
        <span className="text-xs text-gray-400 ml-1">
          / 100 composite risk
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
        <div className="rounded-lg bg-gray-800/60 px-3 py-2">
          <div className="text-xs text-gray-400">Analyzed</div>
          <div className="font-bold text-gray-100" data-testid="stat-analyzed">{total}</div>
        </div>
        <div className="rounded-lg bg-gray-800/60 px-3 py-2">
          <div className="text-xs text-gray-400">Relevant</div>
          <div className="font-bold text-gray-100" data-testid="stat-relevant">{relevant}</div>
        </div>
        <div className="rounded-lg bg-gray-800/60 px-3 py-2">
          <div className="text-xs text-gray-400">False positives removed</div>
          <div className="font-bold text-gray-100" data-testid="stat-fps">{fps}</div>
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">
          India-specific flags
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {INDIA_FLAG_KEYS.map(k => (
            <FlagChip key={k} flagKey={k} isLit={aggFlags[k]}
                       testIdPrefix="india-flag" />
          ))}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-2 pt-2 border-t border-gray-800">
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${CONFIDENCE_STYLES[overallConfidence]}`}>
          Confidence: {overallConfidence}
        </span>
        <span className="text-xs text-gray-400">
          {confidenceReason}
        </span>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function PrecisionAdverseMediaPage() {
  const { user } = useAuth();
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [articles, setArticles] = useState(null);
  const [error, setError] = useState("");

  // ---- role gate ----
  const userRole = String(user?.role || "").toLowerCase();
  if (user && !ALLOWED_ROLES.includes(userRole)) {
    return (
      <div data-testid="precision-page-forbidden"
           className="max-w-2xl mx-auto mt-12 p-8 rounded-xl border
                      border-amber-700 bg-amber-900/20">
        <div className="flex items-center gap-3">
          <Shield className="text-amber-500" />
          <h2 className="text-lg font-semibold text-amber-100">
            Restricted page
          </h2>
        </div>
        <p className="text-sm text-amber-200 mt-2">
          Only Super Admins and Compliance Officers can run precision adverse
          media screening. Contact your tenant administrator.
        </p>
      </div>
    );
  }

  // ---- handlers ----
  const update = (field) => (e) =>
    setForm(f => ({ ...f, [field]: e.target.value }));

  const loadDemo = () => { setForm(DEMO_DATA); setError(""); setArticles(null); };

  const submit = async () => {
    if (!form.subject_name.trim()) {
      setError("Subject name is required.");
      return;
    }
    setError("");
    setArticles(null);
    setLoading(true);
    try {
      const out = await runPrecisionScan(form);
      setArticles(out);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Screening failed.");
    } finally {
      setLoading(false);
    }
  };

  const exportJson = () => {
    if (!articles) return;
    const payload = { subject: form, articles, exported_at: new Date().toISOString() };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `precision-screen-${(form.subject_name || "subject").replace(/\s+/g, "_")}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = () => window.print();

  // ---- render ----
  return (
    <div data-testid="precision-adverse-media-page"
         className="w-full max-w-6xl mx-auto px-3 sm:px-6 py-6 space-y-6 overflow-x-hidden">

      {/* Print-only CSS */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
        }
      `}</style>

      {/* Header */}
      <div className="no-print flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-blue-900/40
                        flex items-center justify-center">
          <Sparkles className="text-blue-300" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-100">
            Precision Adverse-Media Screening
          </h1>
          <p className="text-xs text-gray-400">
            Tier-1-bank-grade analysis · 10 non-negotiable RUDRIK rules · Server-enforced
          </p>
        </div>
      </div>

      {/* Form */}
      <div data-testid="precision-form"
           className="no-print rounded-2xl border border-gray-700
                      bg-[#0d1117] p-6 space-y-4 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold text-gray-300 mb-1">
              Subject name <span className="text-red-500">*</span>
            </label>
            <input
              data-testid="subject-name-input"
              type="text" value={form.subject_name} onChange={update("subject_name")}
              placeholder="e.g. Nirav Modi"
              className="w-full px-3 py-2 rounded-lg border border-gray-700
                         bg-[#080c12] text-gray-100
                         focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-300 mb-1">
              Subject type
            </label>
            <select
              data-testid="subject-type-select"
              value={form.subject_type} onChange={update("subject_type")}
              className="w-full px-3 py-2 rounded-lg border border-gray-700
                         bg-[#080c12] text-gray-100
                         focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="PERSON">PERSON</option>
              <option value="COMPANY">COMPANY</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-300 mb-1">
            Context (optional)
          </label>
          <input
            data-testid="subject-context-input"
            type="text" value={form.subject_context} onChange={update("subject_context")}
            placeholder="e.g. Director, Firestar Diamond, Mumbai"
            className="w-full px-3 py-2 rounded-lg border border-gray-700
                       bg-[#080c12] text-gray-100
                       focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-300 mb-1">
            Articles · paste text or URLs · separate multiple with <code>---</code>
          </label>
          <textarea
            data-testid="articles-textarea"
            value={form.articles} onChange={update("articles")}
            rows={6}
            placeholder="Paste article text or URLs here..."
            className="w-full px-3 py-2 rounded-lg border border-gray-700
                       bg-[#080c12] text-gray-100
                       focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-2">
          <button
            data-testid="run-precision-btn"
            onClick={submit}
            disabled={loading}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg
                       bg-blue-600 hover:bg-blue-700 disabled:opacity-60
                       text-white text-sm font-semibold transition"
          >
            {loading ? <Loader2 className="animate-spin" size={14} /> : <Search size={14} />}
            {loading ? "Screening…" : "Run Precision Screen"}
          </button>
          <button
            data-testid="load-demo-btn"
            type="button" onClick={loadDemo} disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg
                       border border-gray-700
                       text-gray-200 text-sm font-semibold
                       hover:bg-gray-800 transition"
          >
            <Sparkles size={14} /> Load Demo Data
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div data-testid="precision-error"
             className="rounded-xl border border-red-800 bg-red-900/20 p-4
                        text-sm text-red-200">
          {error}
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div data-testid="precision-loading" className="space-y-4">
          <div className="text-sm text-gray-400 italic">
            Scanning ED, CBI, SFIO, SEBI, Google News…
          </div>
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      )}

      {/* Results */}
      {!loading && articles && articles.length > 0 && (
        <>
          <SummaryPanel articles={articles} />

          <div className="no-print flex flex-wrap items-center gap-3 justify-end">
            <button data-testid="export-json-btn" onClick={exportJson}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg
                               border border-gray-700
                               text-gray-200 text-sm font-semibold
                               hover:bg-gray-800 transition">
              <FileJson size={14} /> Export JSON
            </button>
            <button data-testid="export-pdf-btn" onClick={exportPdf}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg
                               border border-gray-700
                               text-gray-200 text-sm font-semibold
                               hover:bg-gray-800 transition">
              <Printer size={14} /> Export PDF
            </button>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {articles.map((item, idx) => <ArticleCard key={idx} idx={idx} item={item} />)}
          </div>
        </>
      )}

      {/* Empty state */}
      {!loading && !articles && !error && (
        <div data-testid="precision-empty"
             className="text-center py-16 text-gray-400">
          <Shield size={36} className="mx-auto mb-3 opacity-50" />
          Enter subject details and paste article sources to begin screening.
        </div>
      )}

      {/* No hits — backend returned 0 articles for some reason */}
      {!loading && articles && articles.length === 0 && (
        <div data-testid="precision-no-hits"
             className="rounded-xl border border-green-800 bg-green-900/20 p-4
                        text-sm text-green-200">
          No adverse media found — subject appears CLEAR.
        </div>
      )}
    </div>
  );
}
